import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import { ContactForm } from '../components/contact-form';
import FaqBlock from '../components/faq-block';
import NewFooter from '../components/new-footer';
import { Box, Grid } from '@mui/material';

import {
  Exports,
  Imports,
  Local
} from '../components/new-home-page/asset-constants';
import FooterCta from '../components/new-home-page/footer-cta';
import GetMobileApp from '../components/new-home-page/get-mobile-app';
import SkeletonLoader from '../components/skeleton-loader';
import Scroller from '../constants/animation';
import {
  ShopnShipIcon,
  QuoteSectionImg,
  FrozenFoodsIcon
} from '../constants/asset-constants';
import { lagosCities } from '../constants/cities';
import { faqs } from '../constants/faqs';
import allowURL, { domestic } from '../constants/seo-array';
import getLocationTexts from '../utilities/get-location-texts';
import getShippingLocationInfo from '../utilities/get-shipping-location';
import NewHeroCarousel from '../components/new-landing-page/new-landing-page-carousel';
import Stat from '../components/new-landing-page/stats-section';
// import DataChartSection from '../components/new-landing-page/data-chart-section';

// const AnimatedBoxes = lazy(() => import('../components/animated-boxes'));
const GetQuotesForm = lazy(() => import('../components/get-quotes-form'));
const Testimonials = lazy(() => import('../components/testimonials'));
const DataChartSection = lazy(
  () => import('../components/new-landing-page/data-chart-section')
);

// const isMobile = window.innerWidth <= 508;

const NewLandingPage = () => {
  const [loaded, setLoaded] = useState(false);
  const [getFaqs] = useState(faqs);
  const history = useHistory();
  const [success, setSuccess] = useState(0);
  const [failure, setFailure] = useState(0);
  const firedRef = useRef(false);

  const pageURL = window?.location?.pathname?.split('/');
  const shippingInfo = getShippingLocationInfo();
  const { senderLocation, receiverLocation } = getLocationTexts(
    shippingInfo,
    history,
    { success, failure, setSuccess, setFailure }
  );
  const openUrl = link => {
    history.push(`/${link}`);
  };

  const removeHyphens = word =>
    word
      ?.split('-')
      ?.map(
        subWord =>
          `${subWord.charAt().toUpperCase()}${subWord.substring(
            1,
            subWord.length
          )}`
      )
      .join(' ');

  const capitalizeWord = word =>
    `${word.charAt().toUpperCase()}${word
      .substring(1, word.length)
      .toLowerCase()}`;

  const isLocalDelivery = pageURL?.[1] === 'local-delivery';
  const lastWordFromURL = pageURL[pageURL.length - 1];
  const isWithinLocalDelivery = isLocalDelivery && domestic(lastWordFromURL);

  const showNigerianCities =
    isLocalDelivery || (senderLocation && domestic(senderLocation));

  const localDeliveryHeader = `Express Delivery Service Within ${capitalizeWord(
    lastWordFromURL
  )}`;

  const localDeliveryDescription = `Send items to ${
    lastWordFromURL === 'abuja'
      ? 'Wuse, Gwagwalada, Maitama'
      : lastWordFromURL === 'ibadan'
        ? 'Dugbe, Bodija, Agodi'
        : 'Yaba, Ikeja, VI, Lekki'
  } and other parts of ${capitalizeWord(
    lastWordFromURL
  )} for as low as N1000. We offer same-day delivery service requests for orders before 2 pm.`;

  const header = isWithinLocalDelivery
    ? localDeliveryHeader
    : isLocalDelivery
      ? `Ship your items within Nigeria with Topship`
      : pageURL?.[1] &&
          removeHyphens(senderLocation) &&
          removeHyphens(receiverLocation)
        ? `Ship from ${removeHyphens(
            senderLocation
          )} to any location in ${removeHyphens(receiverLocation)}`
        : `Ship from ${removeHyphens(senderLocation)} to any location in the world`;

  const description = isWithinLocalDelivery
    ? localDeliveryDescription
    : isLocalDelivery
      ? `Send your items within Lagos, Ibadan & Abuja with
            Topship. It takes 3 - 5 working days to deliver and you
            can be rest assured of the best service when you ship with
            us.`
      : ` Send items to your friends, family, or customers in 
            ${removeHyphens(receiverLocation)} with Topship. Delivery takes 
            ${
              removeHyphens(receiverLocation) === 'United States' ||
              removeHyphens(senderLocation) === 'United States'
                ? '5 - 7'
                : '3 - 5'
            } 
            working days and you can be rest assured of a stress-free
            experience. Request a quote to get started!`;

  // const availableCountries = allCountriesJSONData.filter(
  //   country => country.code !== 'NG' && ourCountries.test(country.code)
  // );

  const availableCities = lagosCities.filter(city =>
    isWithinLocalDelivery
      ? city.name.toLowerCase() !== lastWordFromURL?.toLowerCase()
      : senderLocation
        ? city.name.toLowerCase() !== senderLocation?.toLowerCase()
        : city !== 'Lagos'
  );

  useEffect(() => {
    if (!allowURL(window.location.pathname)) {
      return history.push('/404');
    }

    const handler = () => {
      if (!firedRef.current) {
        firedRef.current = true;
        setLoaded(true);
      }
    };

    const events = ['scroll', 'mousemove', 'keydown', 'touchstart'];
    events.forEach(event =>
      window.addEventListener(event, handler, { once: true })
    );

    return () => {
      events.forEach(event => window.removeEventListener(event, handler));
    };
    //eslint-disable-next-line
  }, []);

  return (
    <div className='new-home-page'>
      <Helmet>
        <meta
          name='description'
          content='Enjoy seamless local and international shipping when you ship with Topship. With flexible pricing at your fingertips.'
        />
        <meta
          name='keywords'
          content='Topship, international shipping, express delivery, logistics delivery company, international delivery, local shipping, cheap international shipping companies, cargo delivery'
        />
        {isWithinLocalDelivery ? (
          <title>{localDeliveryHeader}</title>
        ) : pageURL?.[1] === 'lagos' ||
          pageURL?.[1] === 'abuja' ||
          pageURL?.[1] === 'ibadan' ? (
          <title>
            {`Express Delivery Service from ${removeHyphens(
              senderLocation
            )} to ${
              removeHyphens(receiverLocation) || 'any location in the world'
            }`}
          </title>
        ) : pageURL?.[1] === 'ng' ? (
          <title>
            {`International Shipping from Nigeria to ${
              removeHyphens(receiverLocation) || 'Anywhere in the World'
            }`}
          </title>
        ) : pageURL?.[1] === 'local-delivery' ? (
          <title>Local Delivery to any location in Nigeria</title>
        ) : (
          <title>
            Topship Africa: Local {'&'} International Delivery Service
          </title>
        )}

        <link
          rel='canonical'
          href={`${window.location.origin}${window.location.pathname}`}
        />
      </Helmet>
      <section className='new-home-page__flexible-pricing new__landing-page'>
        {/* <section className='new-home-page__flexible-pricing'> */}
        {/* <HomeCarousel /> */}
        <NewHeroCarousel />
        <div className='new-partners'>
          <p>Our Partners</p>
          <Scroller />
        </div>
      </section>
      <section>
        <Stat />
      </section>

      <section className='new-home-page__banner '>
        <div className='homepage-container'>
          <div className='cta'>
            <div className='cta-contents'>
              {pageURL &&
              (removeHyphens(senderLocation) ||
                removeHyphens(receiverLocation)) ? (
                <>
                  <h1>{header}</h1>

                  <p>{description}</p>
                </>
              ) : (
                <>
                  <h1>
                    Join 18,000+ Nigerians sending and receiving items from
                    their doorsteps to the rest of the world.
                  </h1>
                  <p>
                    Experience stress-free shipping with Topship. Connect with
                    customers and loved ones in Lagos, Abuja, London, Accra,
                    Nairobi, New York, Dubai, and 200+ more cities worldwide. We
                    handle the logistics; you enjoy the convenience.
                  </p>
                </>
              )}
              <div className='banner-cta'>
                <div className='cta-items'>
                  <div className='bttn  cta-item'>
                    <a
                      href={`https://ship.topship.africa/signup`}
                      className='new-home-page__link-button bttn-lg bttn-yellow'
                    >
                      Start Shipping
                    </a>
                  </div>
                </div>
              </div>
            </div>
            {loaded && (
              <Suspense fallback={<SkeletonLoader />}>
                <img
                  src={QuoteSectionImg}
                  alt='gif-animation'
                  className='quote__white__space__gif'
                />
              </Suspense>
            )}
          </div>
          <div id={'request-quote'} tabIndex={0} className='quotes'>
            {loaded && (
              <Suspense fallback={<SkeletonLoader />}>
                <GetQuotesForm shippingInfo={shippingInfo} />
              </Suspense>
            )}
          </div>
        </div>
      </section>
      <section className='interactive__map-section'>
        <h2 className='interactive__section-heading'>
          Flexibility of Choice, Backed by Data
        </h2>
        <p className='interactive__section-sub--text'>
          Gain real - time insights on how African businesses access global
          markets through Topship.
        </p>
        {loaded && (
          <Suspense fallback={<SkeletonLoader />}>
            <DataChartSection />
          </Suspense>
        )}
        {/* <DataChartSection /> */}
      </section>

      <ContactForm serviceTitle={''} isHomeForm />

      <section
        style={{
          position: 'relative'
        }}
        className='new-home-page__banner-info'
      >
        <div
          style={{
            position: 'relative',
            zIndex: '1'
          }}
          className='homepage-container'
        >
          <div className='banner-info-item'>
            <div className='banner-info-items'>
              <div className='services'>
                <h2>Our Services</h2>
                <div className='services-items'>
                  <div
                    onClick={() => openUrl('lagos')}
                    className='services-item'
                  >
                    <div className='small-image'>
                      <img src={Local} alt='local' />
                    </div>
                    <div className='text'>
                      <p className='bold-text'>Nationwide Delivery</p>
                      <p className='light-text'>
                        Doorstep delivery to any city in Nigeria. Pickups are
                        available in Lagos, Abuja & Ibadan.
                      </p>
                    </div>
                  </div>
                  <div
                    onClick={() => openUrl('exports')}
                    className='services-item'
                  >
                    <div className='small-image'>
                      <img src={Exports} alt='exports' />
                    </div>
                    <div className='text'>
                      <p className=' bold-text'>Exports</p>
                      <p className='light-text'>
                        Ship from Nigeria to 200+ cities across the globe
                      </p>
                    </div>
                  </div>
                  <div
                    onClick={() => openUrl('imports')}
                    className='services-item'
                  >
                    <div className='small-image'>
                      <img src={Imports} alt='imports' />
                    </div>
                    <div className='text'>
                      <p className=' bold-text'>Imports</p>
                      <p className='light-text'>
                        Ship anything to Nigeria from anywhere in the world.
                        Express and Budget shipping options available.
                      </p>
                    </div>
                  </div>
                  <div
                    onClick={() => openUrl('shop-&-ship')}
                    className='services-item'
                  >
                    <div className='small-image'>
                      <img
                        style={{
                          width: '40px'
                        }}
                        src={ShopnShipIcon}
                        alt='exports'
                      />
                    </div>
                    <div className='text'>
                      <p className=' bold-text'>Shop & Ship</p>
                      <p className='light-text'>
                        Shop international, ship to Nigeria, and pay NO customs
                        fees. It’s global shopping simplified.
                      </p>
                    </div>
                  </div>
                  <div
                    onClick={() => openUrl('frozen-foods')}
                    className='services-item'
                  >
                    <div className='small-image'>
                      <img src={FrozenFoodsIcon} alt='frozen foods' />
                    </div>
                    <div className='text'>
                      <p className=' bold-text'>Frozen Foods</p>
                      <p className='light-text'>
                        Send and receive meat, poultry, and seafood, delivered
                        within 48 hours. <b>48 hours</b>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {loaded && (
        <Suspense fallback={<SkeletonLoader />}>
          <Testimonials />
        </Suspense>
      )}

      {/* <section
        style={{
          position: 'relative',
          background: '#ffffff'
        }}
        className='new-home-page__banner-info'
      >
        <div
          style={{
            position: 'relative',
            zIndex: '1'
          }}
          className='homepage-container'
        >
          <div className='banner-info-item'>
            <div className='banner-info-items'>
              {loaded && (
                <Suspense fallback={<SkeletonLoader />}>
                  <AnimatedBoxes />
                </Suspense>
              )}
            </div>
          </div>
        </div>
      </section> */}

      {/* <section className='new-home-page__plugin'>
        <div className='plugin-content'>
          <div className='tag'>Designed for merchants</div>
          <h2 className='header'>Plugin</h2>
          <p>
            Say goodbye to booking customer deliveries manually. Compatible with
            WordPress, WooCommerce, Shopify, and more. No coding is required.
          </p>
          <Link to='/plug-ins'>
            <button className='learn-more'>Get Plugin</button>
          </Link>
        </div>

        <div className='image-container'>
          <img src={PluginImg2} alt='developer-img' />
        </div>
      </section> */}

      {/* <section className='new-home-page__api-doc'>
        <div className='api-content'>
          <div className='tag'>Designed for developers</div>
          <h2 className='header'>API</h2>
          <p>
            Integrate global shipping into your app or website. Send custom
            branded shipment notifications to your customers.
            Developer-friendly.
          </p>
          <Link to='/api'>
            <button className='learn-more'>Explore Our API</button>
          </Link>
        </div>

        <div className='image-container'>
          <img src='/assets/api-doc.webp' alt='developer-img' />
        </div>
      </section> */}

      <GetMobileApp />
      <FaqBlock questions={getFaqs} />

      {/* {!showNigerianCities && (
        <section
          id='country-list'
          className='mt-10 new-home-page__country-list'
        >
          <div className='new-home-page__footer__cta__header mb-3'>
            <h2>Send items to 150+ cities</h2>
          </div>
          <p className='fs-3'>Fast, affordable, and great customer service!</p>
          <Grid
            rowSpacing={'20px'}
            style={{ margin: '20px 2% 20px 4%' }}
            container
          >
            {availableCountries.map(country => (
              <Grid key={country.code} item lg={3} md={3} sm={6} xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '16px',
                    '& > img': { mr: 2, flexShrink: 0 },
                    '& > a': { color: 'inherit' }
                  }}
                >
                  <img
                    loading='lazy'
                    width='30'
                    height='20'
                    src={`https://flagcdn.com/w20/${country.code?.toLocaleLowerCase()}.png`}
                    srcSet={`https://flagcdn.com/w40/${country.code?.toLocaleLowerCase()}.png 2x`}
                    alt=''
                  />
                  <a href={`/ng/${country.code.toLowerCase()}`}>
                    {country.name}
                  </a>
                </Box>
              </Grid>
            ))}
          </Grid>
        </section>
      )} */}
      {showNigerianCities && (
        <section className='new-home-page__country-list'>
          <div className='new-home-page__footer__cta__header mb-3'>
            <h2>Send {'&'} Receive items across Nigeria</h2>
          </div>
          <p className='fs-3'>Fast, affordable, and great customer service!</p>
          <Grid
            rowSpacing={'20px'}
            style={{ margin: '20px 2% 20px 4%' }}
            container
          >
            {availableCities.map((city, cityIndex) => (
              <Grid key={cityIndex} item lg={3} md={3} sm={6} xs={12}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '16px',
                    '& > img': { mr: 2, flexShrink: 0 },
                    '& > a': { color: 'inherit' }
                  }}
                >
                  <img
                    loading='lazy'
                    width='30'
                    height='20'
                    src={`https://flagcdn.com/w20/ng.png`}
                    srcSet={`https://flagcdn.com/w40/ng.png 2x`}
                    alt=''
                  />
                  <a
                    href={`/${
                      isWithinLocalDelivery
                        ? lastWordFromURL.toLowerCase()
                        : senderLocation?.toLowerCase() || 'lagos'
                    }${city.link.toLowerCase()}`}
                  >
                    {city.name}
                  </a>
                </Box>
              </Grid>
            ))}
          </Grid>
        </section>
      )}
      {/* <DropOffHubs /> */}
      <FooterCta heading='Create an account and start shipping in minutes' />
      <section className='new-home-page__footer'>
        <NewFooter />
      </section>
    </div>
  );
};

export default NewLandingPage;
